import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { HealthSection } from "./HealthSection";
import { HealthSectionHeader } from "./HealthSectionHeader";
import { HealthLifecycleMobileFlow } from "./HealthLifecycleMobileFlow";
import { HealthLifecyclePanel } from "./HealthLifecyclePanel";
import { HealthLifecycleTimeline } from "./HealthLifecycleTimeline";
import { getLocalized, HealthLang } from "./healthTypes";
import { healthFullWidthPanel } from "./healthLayout";
import {
    getLifecycleScrollMetrics,
    getLifecycleStageIndexFromTransitionScroll,
    getLifecycleTransitionScrollPx,
    getScrollYForLifecycleStage,
    LifecycleScrollMetrics,
    LIFECYCLE_DESKTOP_MQ,
} from "./healthLifecycleConstants";
import { getEffectiveLocationHash } from "../navigation/navPathUtils";
import {
    realignToHealthHashAnchor,
    suppressPageHashClear,
} from "./healthScroll";
import {
    healthLifecyclePanelColumn,
    healthLifecycleSceneHeading,
    healthLifecycleSceneInner,
    healthLifecycleScrollTrack,
    healthLifecycleStickyGrid,
    healthLifecycleStickyScene,
} from "./healthVisualStyles";

interface HealthLifecycleSectionProps {
    data: DataKeys;
}

function LifecycleHeadingBlock({
    sectionId,
    eyebrow,
    title,
    intro,
}: {
    sectionId: string;
    eyebrow: string;
    title: string;
    intro: string;
}) {
    return (
        <div className={healthLifecycleSceneHeading}>
            <HealthSectionHeader
                title={title}
                titleId={`${sectionId}-title`}
                eyebrow={eyebrow}
                subtitle={intro}
                className="mb-0 lg:mb-0"
            />
        </div>
    );
}

export function HealthLifecycleSection({ data }: HealthLifecycleSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const stages: DataKeys[] = data?.stages ?? [];
    const sectionId = data?.id ?? "lifecycle";
    const eyebrow = getLocalized(data?.eyebrow, hLang) || "Care lifecycle";
    const title = getLocalized(data?.title, hLang);
    const intro = getLocalized(data?.intro, hLang);

    const journeyRef = useRef<HTMLDivElement>(null);
    const metricsRef = useRef<LifecycleScrollMetrics | null>(null);
    const [isLgUp, setIsLgUp] = useState(false);
    const [scrollDriveEnabled, setScrollDriveEnabled] = useState(false);
    const [trackHeightPx, setTrackHeightPx] = useState<number | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [scrollDirection, setScrollDirection] = useState(1);
    const activeIndexRef = useRef(0);
    const lastTransitionScrollRef = useRef(0);

    const [clickActiveIndex, setClickActiveIndex] = useState(0);
    const [clickDirection, setClickDirection] = useState(1);
    const clickPrevRef = useRef(0);

    useEffect(() => {
        const desktopMq = window.matchMedia(LIFECYCLE_DESKTOP_MQ);
        const reducedMq = window.matchMedia("(prefers-reduced-motion: reduce)");

        const syncLayout = () => {
            setIsLgUp(desktopMq.matches);
            setScrollDriveEnabled(desktopMq.matches && !reducedMq.matches);
        };

        syncLayout();
        desktopMq.addEventListener("change", syncLayout);
        reducedMq.addEventListener("change", syncLayout);
        return () => {
            desktopMq.removeEventListener("change", syncLayout);
            reducedMq.removeEventListener("change", syncLayout);
        };
    }, []);

    const useScrollDrive = scrollDriveEnabled;
    const resolvedIndex = useScrollDrive ? activeIndex : clickActiveIndex;
    const resolvedDirection = useScrollDrive ? scrollDirection : clickDirection;

    const syncTrackMetrics = useCallback(() => {
        if (!scrollDriveEnabled) {
            metricsRef.current = null;
            setTrackHeightPx(null);
            return;
        }
        const metrics = getLifecycleScrollMetrics();
        metricsRef.current = metrics;
        setTrackHeightPx(metrics.trackHeightPx);
    }, [scrollDriveEnabled]);

    useEffect(() => {
        if (!scrollDriveEnabled) {
            metricsRef.current = null;
            setTrackHeightPx(null);
            return;
        }

        let rafId = 0;
        const scheduleMeasure = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(syncTrackMetrics);
        };

        scheduleMeasure();
        window.addEventListener("resize", scheduleMeasure);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener("resize", scheduleMeasure);
        };
    }, [scrollDriveEnabled, syncTrackMetrics]);

    /** Re-align deep hash targets after the pinned track height is applied. */
    useEffect(() => {
        if (!scrollDriveEnabled || trackHeightPx == null) return;

        const hash = getEffectiveLocationHash(window.location.hash);
        if (!hash) return;

        const targetId = hash.slice(1);
        const target = document.getElementById(targetId);
        const lifecycleEl = document.getElementById(sectionId);
        if (!target || !lifecycleEl) return;

        const targetFollowsLifecycle =
            (lifecycleEl.compareDocumentPosition(target) &
                Node.DOCUMENT_POSITION_FOLLOWING) !==
            0;
        if (!targetFollowsLifecycle) return;

        const realign = () => {
            if (realignToHealthHashAnchor(hash)) {
                suppressPageHashClear();
            }
        };

        realign();
        const timeoutId = window.setTimeout(realign, 0);

        const track = journeyRef.current;
        if (!track) {
            return () => window.clearTimeout(timeoutId);
        }

        let lastHeight = track.offsetHeight;
        const observer = new ResizeObserver(() => {
            const nextHeight = track.offsetHeight;
            if (nextHeight === lastHeight) return;
            lastHeight = nextHeight;
            realign();
        });
        observer.observe(track);

        return () => {
            window.clearTimeout(timeoutId);
            observer.disconnect();
        };
    }, [scrollDriveEnabled, trackHeightPx, sectionId]);

    useEffect(() => {
        if (!scrollDriveEnabled || stages.length === 0) return;

        const track = journeyRef.current;
        if (!track || !metricsRef.current) return;

        let rafId = 0;

        const updateFromScroll = () => {
            const m = metricsRef.current;
            const el = journeyRef.current;
            if (!m || !el) return;

            const transitionScrollPx = getLifecycleTransitionScrollPx(
                el,
                m.stickyTopPx,
                m.transitionDistancePx
            );

            const direction =
                transitionScrollPx >= lastTransitionScrollRef.current ? 1 : -1;
            lastTransitionScrollRef.current = transitionScrollPx;
            setScrollDirection(direction);

            const nextIndex = getLifecycleStageIndexFromTransitionScroll(
                transitionScrollPx,
                m.transitionDistancePx,
                stages.length
            );

            if (nextIndex !== activeIndexRef.current) {
                activeIndexRef.current = nextIndex;
                setActiveIndex(nextIndex);
            }
        };

        const onScrollOrResize = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(updateFromScroll);
        };

        updateFromScroll();
        window.addEventListener("scroll", onScrollOrResize, { passive: true });
        window.addEventListener("resize", onScrollOrResize);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener("scroll", onScrollOrResize);
            window.removeEventListener("resize", onScrollOrResize);
        };
    }, [scrollDriveEnabled, stages.length, trackHeightPx]);

    const selectStage = useCallback(
        (index: number) => {
            const direction = index >= activeIndexRef.current ? 1 : -1;
            setScrollDirection(direction);
            activeIndexRef.current = index;
            setActiveIndex(index);

            const track = journeyRef.current;
            const metrics = metricsRef.current;
            if (!track || !metrics || stages.length <= 1) return;

            const targetY = getScrollYForLifecycleStage(
                track,
                metrics,
                index,
                stages.length
            );
            window.scrollTo({ top: targetY, behavior: "smooth" });
        },
        [stages.length]
    );

    const clickSelectStage = (index: number) => {
        setClickDirection(index >= clickPrevRef.current ? 1 : -1);
        clickPrevRef.current = index;
        setClickActiveIndex(index);
    };

    const resolvedSelect = useScrollDrive ? selectStage : clickSelectStage;

    const activeStage = stages[resolvedIndex];

    const desktopInteraction = (
        <div className={healthLifecycleStickyGrid}>
            <HealthLifecycleTimeline
                stages={stages}
                activeIndex={resolvedIndex}
                onSelect={resolvedSelect}
                lang={hLang}
            />
            <div className={healthLifecyclePanelColumn}>
                <AnimatePresence mode="wait">
                    {activeStage && (
                        <HealthLifecyclePanel
                            key={activeStage.id ?? resolvedIndex}
                            stage={activeStage}
                            lang={hLang}
                            transitionDirection={resolvedDirection}
                            stickyScroll={useScrollDrive}
                            className="w-full text-left [text-align:left]"
                        />
                    )}
                </AnimatePresence>
            </div>
        </div>
    );

    return (
        <HealthSection
            id={sectionId}
            ariaLabelledBy={`${sectionId}-title`}
            compact={!scrollDriveEnabled}
            flushY={scrollDriveEnabled}
            className={`scroll-mt-[var(--health-nav-offset,5rem)] ${
                scrollDriveEnabled ? "lg:overflow-x-clip" : ""
            }`.trim()}
            style={scrollDriveEnabled ? { backgroundColor: "#FFFFFF" } : undefined}
        >
            <div
                className={`${healthFullWidthPanel} flex flex-col gap-8 text-left [text-align:left] lg:gap-10`}
            >
                {scrollDriveEnabled ? (
                    <div
                        ref={journeyRef}
                        className={healthLifecycleScrollTrack}
                        style={
                            trackHeightPx
                                ? { height: `${trackHeightPx}px` }
                                : undefined
                        }
                        aria-hidden={false}
                    >
                        <div className={healthLifecycleStickyScene}>
                            <div className={healthLifecycleSceneInner}>
                                <LifecycleHeadingBlock
                                    sectionId={sectionId}
                                    eyebrow={eyebrow}
                                    title={title}
                                    intro={intro}
                                />
                                {desktopInteraction}
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <LifecycleHeadingBlock
                            sectionId={sectionId}
                            eyebrow={eyebrow}
                            title={title}
                            intro={intro}
                        />
                        {isLgUp ? (
                            <div className="w-full min-w-0">{desktopInteraction}</div>
                        ) : (
                            <HealthLifecycleMobileFlow stages={stages} lang={hLang} />
                        )}
                    </>
                )}
            </div>
        </HealthSection>
    );
}
